// B2 — Tenant directory (mockup panel/PagesOperate.jsx TenantsListPage
// portu). GET /ops/v1/tenants typed client üzerinden; durum filtresi +
// arama backend query param'larına bağlanır. Mockup'taki MRR / sağlık /
// koltuk / risk kolonlarının backend karşılığı yok — görsel port mevcut
// alanlarla yapılır (plan, bölge, modül sayısı, durum, kayıt tarihi);
// eksikler batch notunda.
//
// RBAC: tenants.read (auditor hariç tüm operatör rolleri) — RequireAction
// ile sayfa seviyesinde gate'lenir; backend her durumda gerçek otorite.
//
// KPI satırı yalnız sunucunun `total` değerini basar (lib/tenant-queries.ts);
// sayfa içi sayım "toplam" diye gösterilmez.

import { useEffect, useState } from "react";
import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import {
  Building2,
  ChevronRight,
  CircleCheck,
  CirclePause,
  Download,
  Hourglass,
  Search,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorState,
  FilterPillBar,
  Loading,
  PageHeader,
  SearchInput,
  StatCard,
  type BadgeTone,
} from "@novemai/components";

import { unwrap } from "@/api/envelope";
import { operatorGraph } from "@/api/operator-graph";
import type { paths } from "@/api/types.gen";
import { CursorPager, useCursorStack } from "@/components/CursorPager";
import { RequestExportModal } from "@/components/RequestExportModal";
import { RequireAction } from "@/components/RequireRole";
import { TenantMark } from "@/components/TenantMark";
import { TableScroll } from "@/components/TableScroll";
import { resolveErrorDisplay } from "@/lib/envelope-error-display";
import { tenantCountQueryOptions } from "@/lib/tenant-queries";
import { cn } from "@/lib/utils";

type TenantsQuery = NonNullable<paths["/ops/v1/tenants"]["get"]["parameters"]["query"]>;
type TenantsData = NonNullable<
  paths["/ops/v1/tenants"]["get"]["responses"][200]["content"]["application/json"]["data"]
>;

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const STATE_FILTERS = ["all", "active", "trial", "suspended", "offboarding"] as const;
type StateFilter = (typeof STATE_FILTERS)[number];

const STATE_TONE: Readonly<Record<string, BadgeTone>> = {
  active: "success",
  trial: "info",
  suspended: "danger",
  offboarding: "warning",
};

const PLAN_TONE: Readonly<Record<string, BadgeTone>> = {
  starter: "neutral",
  growth: "info",
  enterprise: "accent",
};

async function fetchTenants(query: TenantsQuery): Promise<TenantsData> {
  return unwrap(operatorGraph.GET("/ops/v1/tenants", { params: { query } }));
}

export function TenantsPage(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <RequireAction
      action="tenants.read"
      loading={<Loading label={t("tenants.loading")} rows={6} />}
      fallback={
        <ErrorState
          title={t("errors.classification.authorization.title")}
          description={t("errors.classification.authorization.fallback")}
        />
      }
    >
      <TenantsContent />
    </RequireAction>
  );
}

function TenantsContent(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const pager = useCursorStack();
  const { cursor, reset: resetCursor } = pager;
  const [exportOpen, setExportOpen] = useState(false);

  // Debounce: her tuş vuruşunda istek atma; TanStack Query key değişimi
  // eski yanıtı zaten düşürür (stale response asla kazanmaz).
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      // Arama değişince yığın çöpe gider: eski imleç yeni sonuç kümesinde bir
      // satıra karşılık gelmeyebilir.
      resetCursor();
    }, SEARCH_DEBOUNCE_MS);
    return (): void => clearTimeout(handle);
  }, [searchInput, resetCursor]);

  const query = useQuery({
    queryKey: ["operator", "tenants", { state: stateFilter, search, cursor }],
    queryFn: () =>
      fetchTenants({
        ...(stateFilter !== "all" ? { state: stateFilter } : {}),
        ...(search !== "" ? { search } : {}),
        limit: PAGE_SIZE,
        ...(cursor !== null ? { cursor } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.tenants ?? [];
  const total = query.data?.total ?? 0;
  const nextCursor = query.data?.next_cursor ?? null;
  // Arama/filtreden bağımsız dizin geneli sayılar: kutucuk etiketi "toplam"
  // diyor, yani görünen sayfanın ya da etkin filtrenin sayısı olamaz.
  const allCount = useQuery(tenantCountQueryOptions("all"));
  const activeCount = useQuery(tenantCountQueryOptions("active"));
  const trialCount = useQuery(tenantCountQueryOptions("trial"));
  const suspendedCount = useQuery(tenantCountQueryOptions("suspended"));
  const countValue = (q: UseQueryResult<number>): string =>
    q.data === undefined ? t("common.empty_value") : `${q.data}`;

  const fmtDate = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso));

  const errorDisplay = query.isError ? resolveErrorDisplay(query.error, t) : null;

  return (
    <div className="mx-auto max-w-[1320px]">
      <PageHeader
        title={t("tenants.title")}
        description={t("tenants.lede")}
        actions={
          <RequireAction action="export.request">
            <Button variant="secondary" onClick={(): void => setExportOpen(true)}>
              <Download size={14} aria-hidden="true" />
              {t("exports.request_button")}
            </Button>
          </RequireAction>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label={t("tenants.kpi.total")}
          value={countValue(allCount)}
          tone="accent"
          icon={<Building2 size={18} />}
        />
        <StatCard
          label={t("tenants.kpi.active")}
          value={countValue(activeCount)}
          tone="success"
          icon={<CircleCheck size={18} />}
        />
        <StatCard
          label={t("tenants.kpi.trial")}
          value={countValue(trialCount)}
          tone="info"
          icon={<Hourglass size={18} />}
        />
        <StatCard
          label={t("tenants.kpi.suspended")}
          value={countValue(suspendedCount)}
          tone="warning"
          icon={<CirclePause size={18} />}
        />
      </div>

      <Card>
        {/* Filtre pill'leri + arama (mockup filter bar) */}
        <div className="flex flex-wrap items-center gap-3 border-b border-[var(--border-subtle)] px-[18px] py-3.5">
          <FilterPillBar
            aria-label={t("tenants.filter_label")}
            value={stateFilter}
            onChange={(next): void => {
              setStateFilter(next);
              resetCursor();
            }}
            items={STATE_FILTERS.map((filter) => ({
              value: filter,
              label: t(`tenants.filter.${filter}`),
            }))}
          />
          <div className="flex-1" />
          <SearchInput
            className="w-64"
            icon={<Search size={14} aria-hidden="true" />}
            value={searchInput}
            onChange={(e): void => setSearchInput(e.target.value)}
            placeholder={t("tenants.search_placeholder")}
            aria-label={t("tenants.search_placeholder")}
          />
        </div>

        {query.isLoading ? (
          <Loading label={t("tenants.loading")} rows={6} />
        ) : errorDisplay !== null ? (
          <ErrorState title={t("tenants.error_title")} description={errorDisplay.description} />
        ) : rows.length === 0 ? (
          <Empty title={t("tenants.empty_title")} description={t("tenants.empty_desc")} />
        ) : (
          <TableScroll>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[var(--border-subtle)] bg-[var(--bg-subtle)]">
                  {(["tenant", "plan", "residency", "modules", "state", "created"] as const).map(
                    (col) => (
                      <th
                        key={col}
                        className="px-4 py-2.5 text-left font-sans text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--fg-muted)]"
                      >
                        {t(`tenants.col.${col}`)}
                      </th>
                    ),
                  )}
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((tenant, i) => {
                  const enabledCount = Object.values(tenant.enabled_modules).filter(Boolean).length;
                  return (
                    <tr
                      key={tenant.tenant_id}
                      className={cn(
                        "hover:bg-[var(--bg-subtle)]",
                        i > 0 && "border-t border-[var(--border-subtle)]",
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <TenantMark name={tenant.company_name} size={32} />
                          <div className="min-w-0">
                            <RouterLink
                              to={`/tenants/${tenant.tenant_id}`}
                              className="font-serif text-[15px] font-medium text-[var(--fg-primary)] no-underline hover:underline"
                            >
                              {tenant.company_name}
                            </RouterLink>
                            <div className="font-mono text-[11px] text-[var(--fg-muted)]">
                              {tenant.tenant_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={PLAN_TONE[tenant.plan_tier] ?? "neutral"}>
                          {t(`tenants.plan.${tenant.plan_tier}`)}
                        </Badge>
                      </td>
                      {/* Bölge bir tanımlayıcı değil etiket: komşu iki sütun
                          (plan, durum) zaten t()'den geçerken bu sütun ham enum
                          basıyordu ("region_tr"). Mono yazı tipi de kimliğe ait,
                          etikete değil. */}
                      <td className="whitespace-nowrap px-4 py-3 font-sans text-[13px] text-[var(--fg-secondary)]">
                        {t(`tenants.residency.${tenant.data_residency}`)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-sans text-[13px] text-[var(--fg-secondary)]">
                        {t("tenants.modules_enabled", { count: enabledCount })}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={STATE_TONE[tenant.state] ?? "neutral"}>
                          {t(`tenants.state.${tenant.state}`)}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-serif text-[12.5px] italic text-[var(--fg-muted)]">
                        {fmtDate(tenant.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RouterLink
                          to={`/tenants/${tenant.tenant_id}`}
                          aria-label={t("tenants.open_row")}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] text-[var(--fg-muted)] hover:bg-[var(--bg-subtle)] hover:text-[var(--fg-primary)]"
                        >
                          <ChevronRight size={16} aria-hidden="true" />
                        </RouterLink>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}

        <CursorPager
          stack={pager}
          shown={rows.length}
          total={total}
          nextCursor={nextCursor}
          busy={query.isLoading}
        />
      </Card>

      {exportOpen && (
        <RequestExportModal initialKind="tenants" onClose={(): void => setExportOpen(false)} />
      )}
    </div>
  );
}
